import { useQuery } from "@apollo/client";
import { Alert, Typography } from "@mui/material";
import { StatCard } from "components/StatCard";
import { WasteScheduleAdherenceSummaryQuery } from "generated-graphql/graphql";
import { StatCardGrid } from "routes/Customer/Waste/components/StatCardGrid";
import { InvoiceTrackingListHeading } from "routes/Customer/Waste/Invoices/InvoiceTrackingListHeading";
import {
  InvoiceTrackingSummaryCard,
  InvoiceTrackingSummarySkeleton,
} from "routes/Customer/Waste/Invoices/InvoiceTrackingSummaryCard";

import { WASTE_SCHEDULE_ADHERENCE_SUMMARY_QUERY } from "./queries";

type Report = WasteScheduleAdherenceSummaryQuery["wasteScheduleAdherence"];
type Summary = Report["summary"];
type VendorSummary = Report["vendors"][number];

function formatPercent(value: number | null | undefined): string {
  return value == null ? "—" : `${Math.round(value * 100)}%`;
}

/**
 * The period's totals, on the waste dashboards' own stat cards.
 *
 * A figure is coloured only where it is a finding against the vendor: service
 * the schedule did not plan. A window nothing evidences may be a missed pickup
 * or a month nobody has imported, and colouring it would claim the one
 * conclusion the audit refuses to draw.
 */
export function SummaryTiles({ summary }: { summary: Summary }) {
  const finding = (count: number) => (count > 0 ? "warning.dark" : "text.primary");
  // The rate is worked out over the windows that could be graded; a window with
  // no invoices is in neither side of it.
  const graded = summary.scheduledWindows - summary.windowsWithoutInvoices;
  return (
    <StatCardGrid testId="schedule-adherence-totals">
      <StatCard
        captionPlacement="top"
        caption="Within schedule"
        value={formatPercent(summary.withinSchedulePercent)}
        subCaption={
          graded === 0 ? "No windows to grade" : `${summary.windowsServed} of ${graded} windows`
        }
      />
      <StatCard
        captionPlacement="top"
        caption="Outside schedule"
        value={String(summary.visitsOutsideWindow)}
        subCaption="Visits"
        valueColor={finding(summary.visitsOutsideWindow)}
      />
      <StatCard
        captionPlacement="top"
        caption="No service shown"
        value={String(summary.windowsWithNoService)}
        subCaption="Windows"
      />
      <StatCard
        captionPlacement="top"
        caption="Unscheduled"
        value={String(summary.unscheduledVisits)}
        subCaption="Visits"
        valueColor={finding(summary.unscheduledVisits)}
      />
      <StatCard
        captionPlacement="top"
        caption="Can't compare"
        value={String(summary.rowsNeedingReview)}
        subCaption="Results"
      />
    </StatCardGrid>
  );
}

/** Vendors with windows this period whose invoices have not been imported at all. */
function vendorsWithoutInvoices(vendors: readonly VendorSummary[]): string[] {
  return vendors
    .filter(({ summary }) => summary.windowsWithoutInvoices > 0)
    .map(({ vendor }) => vendor.name);
}

/**
 * How much of each vendor's plan the imported invoices reach. Stated as a fact
 * rather than judged: a low figure may be missed service or missing invoices,
 * and only the reader can tell which.
 */
function InvoiceCoverage({ vendors }: { vendors: readonly VendorSummary[] }) {
  const covered = vendors.filter(
    ({ summary }) => summary.scheduledWindows > summary.windowsWithoutInvoices
  );
  if (covered.length === 0) return null;
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Invoices show billed service for{" "}
      {covered
        .map(
          ({ vendor, summary }) =>
            `${summary.windowsWithBilledService} of ${summary.scheduledWindows} ${vendor.name} windows`
        )
        .join(" and ")}
      .
    </Typography>
  );
}

function NoInvoicesAlert({ vendors, all }: { vendors: string[]; all: boolean }) {
  if (vendors.length === 0) return null;
  return (
    <Alert severity="info" sx={{ mt: 1 }}>
      {all
        ? "No invoices have been imported for this period, so every scheduled window is unresolved for that one reason rather than for anything the schedule says. Import the period's invoices to grade it, or choose a period that has them."
        : `No ${vendors.join(" or ")} invoices have been imported for this period, so those windows are unresolved and left out of the within-schedule rate. Import them to grade the period in full.`}
    </Alert>
  );
}

/**
 * The totals for the whole period, asked for on the vendor and period alone so
 * that filtering the rows below cannot move them.
 */
export function PeriodTotals({
  tenantId,
  from,
  through,
}: {
  tenantId: string;
  from: string;
  through: string;
}) {
  const { data, loading } = useQuery(WASTE_SCHEDULE_ADHERENCE_SUMMARY_QUERY, {
    variables: { tenantId, from, through },
    skip: !tenantId || from === "",
  });

  const summary = data?.wasteScheduleAdherence.summary;
  if (loading && !summary) {
    return <InvoiceTrackingSummarySkeleton count={5} />;
  }
  if (!summary) return null;

  return (
    <InvoiceTrackingSummaryCard>
      <SummaryTiles summary={summary} />
    </InvoiceTrackingSummaryCard>
  );
}

/**
 * What the reader is looking at, and anything they need to know before reading
 * it. Sits with the list rather than with the totals, because it explains the
 * rows: the totals are on their own card above.
 *
 * Reads the same query as {@link PeriodTotals}; Apollo serves the second caller
 * from cache rather than asking twice.
 */
export function ReportIntro({
  tenantId,
  from,
  through,
}: {
  tenantId: string;
  from: string;
  through: string;
}) {
  const { data } = useQuery(WASTE_SCHEDULE_ADHERENCE_SUMMARY_QUERY, {
    variables: { tenantId, from, through },
    skip: !tenantId || from === "",
  });
  const report = data?.wasteScheduleAdherence;
  const missing = report == null ? [] : vendorsWithoutInvoices(report.vendors);
  const all =
    report != null &&
    report.summary.scheduledWindows > 0 &&
    report.summary.windowsWithoutInvoices === report.summary.scheduledWindows;
  // The heading and what a row is do not depend on the figures, so they are
  // here from the first paint: waiting would shunt the list down the page when
  // the totals land.
  return (
    <>
      <InvoiceTrackingListHeading title="Schedule Adherence">
        Each row is one scheduled visit or billed service, compared against the vendor&apos;s
        published schedule.
      </InvoiceTrackingListHeading>
      <NoInvoicesAlert vendors={missing} all={all} />
      {report != null && !all ? <InvoiceCoverage vendors={report.vendors} /> : null}
    </>
  );
}
